import useHttpProvider from "@/common/hooks/use-http";
import { AuthenticationProvider } from "@/common/providers/authentication";
import { SignUpRoute } from "@/features/authentication/route";
import { GiveawayDetailsRoute } from "@/features/home/route";
import { useSubscription } from "@/features/payments/providers/subscription";
import { buildCheckoutEntryUrl } from "@/features/payments/route";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Entry } from "../models/entry";
import * as EntriesServices from "../services/entries";

const JOIN_THE_CLUB_ELEMENT_ID = "join-the-club";

function useEntries() {
	const { isAuthenticated } = AuthenticationProvider;
	const { http } = useHttpProvider();
	const { hasActiveSubscription } = useSubscription();

	const navigate = useNavigate();
	const queryParams = new URLSearchParams(location.search);
	const id = queryParams.get("id");

	const [entries, setEntries] = useState<Entry[]>([]);
	const [showSubscribeInsteadDialog, setShowSubscribeInsteadDialog] =
		useState<boolean>(false);
	const pendingEntryRef = useRef<Entry | null>(null);

	const getEntries = useCallback(async () => {
		try {
			if (id) {
				const response = await EntriesServices.getEntriesByDrawId(
					http,
					parseInt(id)
				);
				setEntries(response);
			}
		} catch {}
	}, [http, id]);

	const goToSignUp = useCallback(
		(entry: Entry) => {
			const params = new URLSearchParams();
			params.set("from", GiveawayDetailsRoute + "?id=" + id);
			params.set("skipPlans", "true");
			sessionStorage.setItem("returningFromSignupEntry", JSON.stringify(entry));
			return navigate(SignUpRoute + "?" + params.toString());
		},
		[navigate, id]
	);

	const proceedToCheckout = useCallback(
		(entry: Entry) => {
			navigate(buildCheckoutEntryUrl(entry.id), { state: { entry } });
		},
		[navigate]
	);

	const onEntrySelected = useCallback(
		(entry: Entry) => {
			if (!isAuthenticated || !hasActiveSubscription) {
				pendingEntryRef.current = entry;
				setShowSubscribeInsteadDialog(true);
				return;
			}

			proceedToCheckout(entry);
		},
		[isAuthenticated, hasActiveSubscription, proceedToCheckout]
	);

	const onProceedToEntryCheckout = useCallback(() => {
		const entry = pendingEntryRef.current;
		if (!entry) return;
		setShowSubscribeInsteadDialog(false);
		if (!isAuthenticated) {
			goToSignUp(entry);
			return;
		}
		proceedToCheckout(entry);
	}, [isAuthenticated, goToSignUp, proceedToCheckout]);

	const onViewPlans = useCallback(() => {
		setShowSubscribeInsteadDialog(false);
		pendingEntryRef.current = null;
		const target = document.getElementById(JOIN_THE_CLUB_ELEMENT_ID);
		if (target) {
			target.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	}, []);

	const handleReturningUserEntry = useCallback(() => {
		const returningFromSignUp = sessionStorage.getItem("returningFromSignup");
		if (returningFromSignUp) {
			sessionStorage.removeItem("returningFromSignup");
			const pickedEntry = sessionStorage.getItem("returningFromSignupEntry");

			if (pickedEntry) {
				sessionStorage.removeItem("returningFromSignupEntry");
				const entry = JSON.parse(pickedEntry);
				proceedToCheckout(entry);
			}
		}
	}, [proceedToCheckout]);

	useEffect(() => {
		getEntries();
		handleReturningUserEntry();
	}, [getEntries, handleReturningUserEntry]);

	return {
		entries,
		onEntrySelected,
		showSubscribeInsteadDialog,
		setShowSubscribeInsteadDialog,
		onProceedToEntryCheckout,
		onViewPlans,
	};
}

export default useEntries;
